import { useRef,useState } from "react";

import { faImage, faSpinner,faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLanguage } from "../../contexts/LanguageContext";
import { apiService } from "../../services/apiService";

import "./PhotoUpload.css";

const PhotoUpload = ({ eventId, onPhotoUploaded, onError, user }) => {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadQueue, setUploadQueue] = useState([]); // Nueva prop para manejar cola
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = async (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      onError("Please select valid image files");
      return;
    }

    if (imageFiles.length > 1) {
      await uploadMultipleFiles(imageFiles);
    } else {
      await uploadFile(imageFiles[0]);
    }
  };

  const uploadMultipleFiles = async (files) => {
    try {
      setUploading(true);
      setUploadQueue(files);
      let uploadedPhotos = [];
      let failedUploads = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const progress = ((i + 1) / files.length) * 100;
        setUploadProgress(progress);

        try {
          const metadata = await getImageMetadata(file);
          const result = await apiService.uploadEventPhoto(eventId, file, {
            ...metadata,
            uploaderName: user.name,
          });

          uploadedPhotos.push(result.photo);

          if (i < files.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          failedUploads++;
        }
      }

      // Delay adicional antes de mostrar resultados para evitar saltos
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Notificar una sola vez con todos los resultados
      if (uploadedPhotos.length > 0) {
        // Llamar onPhotoUploaded solo una vez para disparar una recarga
        onPhotoUploaded({
          batch: true,
          count: uploadedPhotos.length,
          photos: uploadedPhotos,
        });
      }

      // Toast único con resumen
      if (failedUploads > 0) {
        onError(
          `${uploadedPhotos.length} photos uploaded successfully, ${failedUploads} failed`
        );
      }
    } catch (error) {
      console.error("Batch upload error:", error);
      onError(error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadQueue([]);
    }
  };

  const uploadFile = async (file) => {
    try {
      if (uploading) return;
      setUploading(true);
      setUploadProgress(0);

      const metadata = await getImageMetadata(file);

      // Simular progreso gradual para archivo único
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const result = await apiService.uploadEventPhoto(eventId, file, {
        ...metadata,
        uploaderName: user.name,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        onPhotoUploaded(result.photo);
        setUploading(false);
        setUploadProgress(0);
      }, 300);
    } catch (error) {
      console.error("Upload error:", error);
      onError(error.message);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getImageMetadata = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          device: navigator.userAgent,
          orientation: img.width > img.height ? "landscape" : "portrait",
        });
      };
      img.onerror = () => resolve({});
      img.src = URL.createObjectURL(file);
    });
  };

  return (
    <div className="photo-upload">
      <div
        className={`upload-zone ${isDragging ? "dragging" : ""} ${
          uploading ? "uploading" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {uploading ? (
          <div className="upload-progress">
            <FontAwesomeIcon icon={faSpinner} className="upload-icon" spin />
            {uploadQueue.length > 1 ? (
              <p>
                {t("dashboard.upload.uploading")} {uploadQueue.length} {t("dashboard.upload.photos")}...{" "}
                {Math.round(uploadProgress)}%
              </p>
            ) : (
              <p>
                {t("dashboard.upload.uploading")} {Math.round(uploadProgress)}%
              </p>
            )}
          </div>
        ) : (
          <div className="upload-content">
            <FontAwesomeIcon icon={faUpload} className="upload-icon" />
            <h3>{t("dashboard.upload.title")}</h3>
            <p>{t("dashboard.upload.description")}</p>
            <button className="upload-btn">
              <FontAwesomeIcon icon={faImage} />
              {t("dashboard.upload.uploadButton")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUpload;
